import React, { ReactNode, forwardRef } from 'react';
import classnames from 'classnames';

import { Tooltip, ButtonBase } from '@material-ui/core';
import { makeStyles, createStyles, darken } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';

export const useStyles = makeStyles((theme) => createStyles({
  tooltipPlacementRight: {
    left: theme.spacing(-1.25)
  },
  iconItemContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderLeft: '2px solid transparent'
  },
  iconItemContainerActive: { borderColor: theme.palette.common.white },
  iconItem: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    position: 'relative',
    width: theme.spacing(5),
    height: theme.spacing(5),
    margin: theme.spacing(0.5, 0),
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: '18px',
    borderRadius: theme.shape.borderRadius * 4,
    '& > svg': {
      fill: theme.palette.grey[200]
    },
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: darken(theme.palette.grey[900], 0.3),
      color: theme.palette.primary.main
    }
  },
  iconItemActive: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightSemibold,
    '& > svg': {
      fill: theme.palette.common.white
    }
  },
  header: {
    textDecoration: 'none',
    pointerEvents: 'none',
    opacity: '1 !important', // Avoid opacity when element is disabled
    '& > span': {
      fontWeight: theme.typography.fontWeightSemibold,
      fontSize: 22
    },
    '&:focus, &:hover, &:active': {
      textDecoration: 'none'
    }
  }
}));

export type ItemProps = {
  id: string,
  label: string,
  href?: string | undefined,
  icon?: string,
  className?: string | undefined,
  active?: boolean,
  disabled?: boolean,
  tooltipLabel?: string,
  subitems?: Array<ItemProps>
}

export type ListItemProps = {
  item: ItemProps
  onClick?: any,
  disableHref?: boolean
  children?: ReactNode
}

const IconItemLink = forwardRef<HTMLDivElement, ListItemProps>(({
  item,
  onClick,
  disableHref,
  ...otherProps
}: ListItemProps, ref) => {
  const classes = useStyles();
  const buttonBaseProps = !disableHref && { href: item.href };
  return (
    <div
      ref={ref}
      {...otherProps}
      className={classnames(
        classes.iconItemContainer,
        {
          [classes.iconItemContainerActive]: item.active
        }
      )}
    >
      <ButtonBase
        onClick={onClick}
        disableRipple
        disableTouchRipple
        className={classnames(
          classes.iconItem,
          {
            [classes.iconItemActive]: item.active
          }
        )}
        {...buttonBaseProps}
      >
        {
          item.icon && (<Icon size='md' type={item.icon} />)
        }
      </ButtonBase>
    </div>
  );
});

const RailNavigationListItem = ({ item }: ListItemProps) => {
  const classes = useStyles();

  return (
    <Tooltip
      title={<Translate content={item.label} />}
      arrow
      placement='right'
      classes={{ tooltipPlacementRight: classes.tooltipPlacementRight }}
    >
      <IconItemLink item={item} />
    </Tooltip>
  );
};

export default RailNavigationListItem;
